import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSupabase } from '../../contexts/SupabaseContext';
import { PieChart } from '@mui/x-charts/PieChart';
import { chartPalette } from '../../theme';
import { useTheme } from '@mui/material';
import GuidedTour from '../../components/tour/GuidedTour';
import TourButton from '../../components/tour/TourButton';
import useTour from '../../components/tour/useTour';
import { TOURS } from '../../components/tour/tours';

// Colors for the pie chart - matching AssetClassDetails
const COLORS = [
  '#1976d2',  // Blue
  '#2e7d32',  // Green
  '#c2185b',  // Pink
  '#f57c00',  // Orange
  '#7b1fa2',  // Purple
  '#00796b',  // Teal
  '#d32f2f',  // Red
  '#fbc02d',  // Yellow
  '#455a64',  // Blue Grey
  '#5d4037',  // Brown
  '#0097a7',  // Cyan
  '#6d4c41',  // Deep Brown
];

const Debt = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { supabase, user } = useSupabase();
  const [loading, setLoading] = useState(true);
  const tour = useTour('inv-debt', { ready: !loading });
  const [investments, setInvestments] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: 'value', direction: 'desc' });

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });

    const sortedData = [...investments].sort((a, b) => {
      if (key === 'name') {
        return direction === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (key === 'value') {
        return direction === 'asc' 
          ? a.value - b.value
          : b.value - a.value;
      } else if (key === 'allocation') {
        const aAllocation = totalValue > 0 ? (a.value / totalValue) * 100 : 0;
        const bAllocation = totalValue > 0 ? (b.value / totalValue) * 100 : 0;
        return direction === 'asc' 
          ? aAllocation - bAllocation
          : bAllocation - aAllocation;
      }
      return 0;
    });

    setInvestments(sortedData);
  };

  const fetchInvestments = useCallback(async () => {
    if (!user) {
      console.log('No user found, cannot fetch investments');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching investments for user:', user.id);

      // Fetch NCD investments
      let ncdTotal = 0;
      try {
        const { data: ncdData, error: ncdError } = await supabase
          .from('ncd_investments')
          .select('amount_invested')
          .eq('user_id', user.id);

        if (ncdError) {
          console.error('Error fetching NCD data:', ncdError);
        } else {
          ncdTotal = ncdData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
          console.log('NCD total:', ncdTotal);
        }
      } catch (err) {
        console.error('Exception fetching NCD:', err);
      }

      // Fetch Fixed Deposit investments
      let fdTotal = 0;
      try {
        const { data: fdData, error: fdError } = await supabase
          .from('fixed_deposit_investments')
          .select('amount_invested')
          .eq('user_id', user.id);

        if (fdError) {
          console.error('Error fetching Fixed Deposit data:', fdError);
        } else {
          fdTotal = fdData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
          console.log('Fixed Deposit total:', fdTotal);
        }
      } catch (err) {
        console.error('Exception fetching Fixed Deposit:', err);
      }

      // Fetch EPF investments
      let epfTotal = 0;
      try {
        const { data: epfData, error: epfError } = await supabase
          .from('epf_investments')
          .select('amount_invested')
          .eq('user_id', user.id);

        if (epfError) {
          console.error('Error fetching EPF data:', epfError);
        } else {
          epfTotal = epfData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
          console.log('EPF total:', epfTotal);
        }
      } catch (err) {
        console.error('Exception fetching EPF:', err);
      }

      // Fetch PPF investments
      let ppfTotal = 0;
      try {
        const { data: ppfData, error: ppfError } = await supabase
          .from('ppf_investments')
          .select('amount_invested')
          .eq('user_id', user.id);

        if (ppfError) {
          console.error('Error fetching PPF data:', ppfError);
        } else {
          ppfTotal = ppfData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
          console.log('PPF total:', ppfTotal);
        }
      } catch (err) {
        console.error('Exception fetching PPF:', err);
      }

      // Fetch Government Bonds investments
      let govtBondsTotal = 0;
      try {
        const { data: govtBondsData, error: govtBondsError } = await supabase
          .from('govt_bond_investments')
          .select('amount_invested')
          .eq('user_id', user.id);

        if (govtBondsError) {
          console.error('Error fetching Government Bonds data:', govtBondsError);
        } else {
          govtBondsTotal = govtBondsData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
          console.log('Government Bonds total:', govtBondsTotal);
        }
      } catch (err) {
        console.error('Exception fetching Government Bonds:', err);
      }

      // Fetch Corporate Bonds investments
      let corpBondsTotal = 0;
      try {
        const { data: corpBondsData, error: corpBondsError } = await supabase
          .from('corp_bond_investments')
          .select('amount_invested')
          .eq('user_id', user.id);

        if (corpBondsError) {
          console.error('Error fetching Corporate Bonds data:', corpBondsError);
        } else {
          corpBondsTotal = corpBondsData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
          console.log('Corporate Bonds total:', corpBondsTotal);
        }
      } catch (err) {
        console.error('Exception fetching Corporate Bonds:', err);
      }

      // Fetch Debt Mutual Funds investments
      let debtMutualFundTotal = 0;
      try {
        const { data: debtMutualFundData, error: debtMutualFundError } = await supabase
          .from('debt_mutual_fund_investments')
          .select('amount_invested')
          .eq('user_id', user.id);

        if (debtMutualFundError) {
          console.error('Error fetching Debt Mutual Funds data:', debtMutualFundError);
        } else {
          debtMutualFundTotal = debtMutualFundData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
          console.log('Debt Mutual Funds total:', debtMutualFundTotal);
        }
      } catch (err) {
        console.error('Exception fetching Debt Mutual Funds:', err);
      }

      // Fetch NPS Debt investments
      let npsDebtTotal = 0;
      try {
        const { data: npsDebtData, error: npsDebtError } = await supabase
          .from('nps_debt_investments')
          .select('amount_invested')
          .eq('user_id', user.id);

        if (npsDebtError) {
          console.error('Error fetching NPS Debt data:', npsDebtError);
        } else {
          npsDebtTotal = npsDebtData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
          console.log('NPS Debt total:', npsDebtTotal);
        }
      } catch (err) {
        console.error('Exception fetching NPS Debt:', err);
      }

      // Fetch Other Debt investments
      let debtOthersTotal = 0;
      try {
        const { data: debtOthersData, error: debtOthersError } = await supabase
          .from('debt_others_investments')
          .select('amount_invested')
          .eq('user_id', user.id);

        if (debtOthersError) {
          console.error('Error fetching Other Debt data:', debtOthersError);
        } else {
          debtOthersTotal = debtOthersData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
          console.log('Other Debt total:', debtOthersTotal);
        }
      } catch (err) {
        console.error('Exception fetching Other Debt:', err);
      }

      // Fetch Recurring Deposit investments
      let rdTotal = 0;
      try {
        const { data: rdData, error: rdError } = await supabase
          .from('recurring_deposit_investments')
          .select('monthly_investment, start_date')
          .eq('user_id', user.id);
        if (rdError) {
          console.error('Error fetching Recurring Deposit data:', rdError);
        } else {
          // Calculate current contribution value for each RD and sum
          rdTotal = rdData.reduce((sum, item) => {
            const start = new Date(item.start_date);
            const today = new Date();
            
            // Calculate months difference
            const monthsDiff = (today.getFullYear() - start.getFullYear()) * 12 + 
                            (today.getMonth() - start.getMonth());
            
            // If start date is in future, return 0
            if (monthsDiff < 0) return sum;
            
            // If start date day is greater than today's date, subtract one month
            // as the current month's installment wouldn't be counted
            const adjustedMonths = start.getDate() > today.getDate() ? monthsDiff : monthsDiff + 1;
            
            // Calculate total contribution for this RD
            const contribution = item.monthly_investment * Math.max(0, adjustedMonths);
            return sum + contribution;
          }, 0);
          console.log('Recurring Deposit current value:', rdTotal);
        }
      } catch (err) {
        console.error('Exception fetching Recurring Deposit:', err);
      }

      // Format data for display
      const investmentsList = [
        {
          id: 'ncd',
          name: 'Non-Convertible Debentures',
          value: ncdTotal,
          path: '/investments/debt/ncd'
        },
        {
          id: 'fixed-deposit',
          name: 'Fixed Deposits',
          value: fdTotal,
          path: '/investments/debt/fd'
        },
        {
          id: 'recurring-deposit',
          name: 'Recurring Deposits',
          value: rdTotal,
          path: '/investments/debt/recurring-deposit'
        },
        {
          id: 'epf',
          name: 'Employee Provident Fund',
          value: epfTotal,
          path: '/investments/debt/epf'
        },
        {
          id: 'ppf',
          name: 'Public Provident Fund',
          value: ppfTotal,
          path: '/investments/debt/ppf'
        },
        {
          id: 'govt-bonds',
          name: 'Government Bonds',
          value: govtBondsTotal,
          path: '/investments/debt/govt-bonds'
        },
        {
          id: 'corp-bonds',
          name: 'Corporate Bonds',
          value: corpBondsTotal,
          path: '/investments/debt/corp-bonds'
        },
        {
          id: 'debt-mutual-fund',
          name: 'Debt Mutual Funds',
          value: debtMutualFundTotal,
          path: '/investments/debt/mutual-fund'
        },
        {
          id: 'nps-debt',
          name: 'NPS Debt Component',
          value: npsDebtTotal,
          path: '/investments/debt/nps'
        },
        {
          id: 'debt-others',
          name: 'Other Debt Investments',
          value: debtOthersTotal,
          path: '/investments/debt/others'
        }
      ];

      // Sort by value (descending)
      investmentsList.sort((a, b) => b.value - a.value);
      
      setInvestments(investmentsList);
      const total = investmentsList.reduce((sum, item) => sum + item.value, 0);
      setTotalValue(total);
      console.log('Total value of all investments:', total);

    } catch (error) {
      console.error('Error in fetchInvestments:', error);
      setInvestments([]);
      setTotalValue(0);
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    fetchInvestments();
  }, [fetchInvestments]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header with back button - matching AssetClassDetails style */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          onClick={() => navigate('/investments')}
          sx={{
            mr: 2,
            color: theme.palette.grey[500],
            '&:hover': { color: theme.palette.primary.main }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box data-tour="inv-class-header">
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Debt
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Total: {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
              maximumFractionDigits: 0
            }).format(totalValue)}
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <TourButton onClick={tour.start} />
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Left side - Table with AssetClassDetails styling */}
          <Grid item xs={12} md={8}>
            <Paper
              data-tour="inv-class-products"
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                backdropFilter: 'blur(20px)',
                boxShadow: 'var(--fx-card-shadow)'
              }}
            >
              <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Products
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        onClick={() => handleSort('name')}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Product Name
                        {sortConfig.key === 'name' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right"
                        onClick={() => handleSort('value')}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Value
                        {sortConfig.key === 'value' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right"
                        onClick={() => handleSort('allocation')}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Allocation
                        {sortConfig.key === 'allocation' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {investments.map((investment, index) => (
                      <TableRow
                        key={investment.id}
                        onClick={() => navigate(investment.path)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: COLORS[index % COLORS.length],
                                mr: 2
                              }}
                            />
                            {investment.name}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0
                          }).format(investment.value)}
                        </TableCell>
                        <TableCell align="right">
                          {totalValue > 0 ? ((investment.value / totalValue) * 100).toFixed(1) : '0'}%
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Add Total Row */}
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(totalValue)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        100%
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Distribution Chart */}
          <Grid item xs={12} md={4}>
            <Paper
              data-tour="inv-class-chart" sx={{ 
              height: '100%', 
              borderRadius: 2, 
              p: 2 
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Distribution</Typography>
              {investments.length > 0 && (
                <PieChart colors={chartPalette}
                  series={[
                    {
                      data: investments.map(item => ({
                        id: item.id,
                        value: item.value,
                        label: item.name
                      })),
                      highlightScope: { faded: 'global', highlighted: 'item' },
                      faded: { innerRadius: 30, additionalRadius: -30 },
                      label: false,
                      labelLine: false,
                    },
                  ]}
                  height={400}
                  legend={{
                    hidden: true
                  }}
                  margin={{
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0
                  }}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
      <GuidedTour steps={TOURS['inv-debt']} open={tour.open} onClose={tour.close} />
    </Box>
  );
};

export default Debt;